// 오라클 성능 분석 › AWR 리포트 (2026-10-02, 명세 05-monitoring 「AWR 리포트 분석」)
//
//  1. DB·인스턴스·스냅샷 고르기 — 저장소에 들여온 **다른 DB 의 스냅샷**도 고를 수 있다(dbid 별)
//  2. 원본 리포트 — 오라클 AWR_REPORT_TEXT 그대로(텍스트), 파일로 저장
//  3. 분석 요약 — 섹션을 골라(예: 대기 이벤트만, SQL 만) **규칙 기반**과 **AI** 를 고른 대로 나란히 돌린다.
//     둘은 같은 사실(/awr/facts)을 본다 — 숫자가 다르면 판단 차이인지 데이터 차이인지 가릴 수 없다.
//  4. 동적 뷰 부가 분석 — **지금 접속한 인스턴스**를 고른 경우에만(다른 DB·노드의 스냅샷에 지금의 v$ 를 붙이면 거짓)
//
// SQL 원문은 AI 로 **기본 안 보낸다**(사용자 결정 2026-10-02) — 「SQL 원문 포함」 을 켠 분석에서만 보내고, 보냈다고 적는다.
// 조회는 모두 창의 세션 줄(monRun)로 — 한 세션은 한 번에 한 문장이다.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SqlSourceView from './SqlSourceView';
import { QueryViewButton } from './QueryView';
import SnapRangeBar from './SnapRangeBar';
import { FiFileText, FiPlay, FiDownload, FiAlertTriangle, FiInfo, FiCpu } from 'react-icons/fi';
import { formatApiError } from '../../utils/formatters';
import { saveTextFile } from '../../utils/desktopFile';
import { loadSection } from '../../utils/appSettings';
import { summarizeAwr } from '../../services/assistantClient';
import { AI_ENABLED } from '../../config/features';
import './AwrReportPanel.css';

const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
const AWR_API = `${API_ROOT}/sql-developer/monitoring/awr`;

// 섹션 키는 백엔드 awrAnalysis.SECTIONS 와 같아야 한다 — 다르면 고른 섹션이 조용히 빠진다
const SECTION_KEYS = ['loadProfile', 'timeModel', 'waitEvents', 'topSql', 'segLogical', 'segPhysical', 'tablespaceIo', 'efficiency'];
// 진단 의견을 표 **아래**에 두는 섹션 — 표가 넓어 오른쪽에 자리가 없다
const OPINION_BELOW = new Set(['topSql', 'live']);
const DAYS = [1, 3, 7, 14, 31];
// 막대(와 스냅샷 목록)는 늘 이만큼을 받는다 — 기간은 그 안에서 구간을 정하는 것이다. 기간마다 목록을 다시 받으면 막대가 기간과 같아져
// 「기간을 바꿔도 막대가 안 바뀐다」 가 됐다(2026-10-14 신고)
const BAR_DAYS = 31;
// 대기 분류 → 조언 키. 모르는 분류는 other
const WAIT_CLASS_KEY = {
    'User I/O': 'userIo', 'System I/O': 'systemIo', Commit: 'commit', Concurrency: 'concurrency',
    Application: 'application', Configuration: 'configuration', Cluster: 'cluster', Network: 'network',
};

/** 수치 — 작은 값을 0 으로 뭉개지 않는다. 없는 값은 「-」 */
function fmt(v, digits = 2) {
    if (v == null || Number.isNaN(Number(v))) return '-';
    const n = Number(v);
    const abs = Math.abs(n);
    const d = abs !== 0 && abs < 1 ? Math.max(digits, 3) : digits;
    return n.toLocaleString(undefined, { maximumFractionDigits: d });
}
const shortTime = (iso) => (iso ? String(iso).replace('T', ' ').slice(0, 16) : '');

/**
 * 기간을 고르면 그 기간 전체를 구간으로 잡는다(2026-10-14 — 예전에는 기간을 바꿔도 「가장 최근 한 구간」 그대로여서 바뀌지 않는 것처럼 보였다).
 * 끝 = 가장 최근 스냅샷, 시작 = 그와 **같은 기동** 안에서 가장 오래된 스냅샷 — 재기동을 넘는 구간은 오라클 리포트도 거절한다.
 * 목록은 최신부터(DESC) 온다.
 */
export function defaultRange(list, days) {
    if (!Array.isArray(list) || list.length < 2) return { begin: '', end: list && list[0] ? String(list[0].snapId) : '' };
    const end = list[0];
    // 기간의 시작 — 가장 최근 스냅샷에서 days 일 전. 막대는 받아 온 전체(최대 31일)라 기간을 바꾸면 손잡이가 움직인다(2026-10-14)
    const cutoff = days ? Date.parse(end.endTime) - days * 86400000 : -Infinity;
    const inPeriod = (x) => !Number.isFinite(cutoff) || !x.endTime || Date.parse(x.endTime) >= cutoff;
    let i = 1;
    while (i + 1 < list.length && (!end.startup || list[i + 1].startup === end.startup) && inPeriod(list[i + 1])) i += 1;
    if (end.startup && list[i].startup !== end.startup) i = 0;
    return { begin: i > 0 ? String(list[i].snapId) : '', end: String(end.snapId) };
}

/** 세그먼트 이름 — 지워진 객체는 이름이 없다(obj# 만) */
const segLabel = (x, t) => (x.objectName
    ? `${x.owner ? `${x.owner}.` : ''}${x.objectName}${x.subobjectName ? ` (${x.subobjectName})` : ''}`
    : t('monitoring:awr.droppedObj', { n: x.objNo }));

function apiError(err, t) {
    // 어시스턴트 클라이언트(fetch)는 errorType 을 err 에 바로 싣는다
    if (err && err.errorType && !err.response) {
        const key = `common:errors.${err.errorType}`;
        const tr = t(key, (err.body && err.body.params) || {});
        if (tr !== key && tr !== key.split(':')[1]) return tr;
        return err.message;
    }
    return formatApiError(err, t);
}

export default function AwrReportPanel({ selectedConnId, monParams, monRun }) {
    const { t, i18n } = useTranslation();
    // 조회 effect 안의 오류 문구용 — t 를 의존성에 넣으면 t 가 바뀔 때마다 처음부터 다시 불러온다
    const tRef = useRef(t);
    tRef.current = t;
    // 예시는 기본 꺼짐 — 연결이 없어도 켜 두지 않는다(2026-10-03, 모든 창 공통). 이 패널은 창이 닫히면 함께 사라져 매번 꺼진 채로 시작한다
    const [useDemo, setUseDemo] = useState(false);
    const run = useCallback((fn) => (monRun ? monRun(fn) : Promise.resolve().then(fn)), [monRun]);
    const target = useCallback((extra) => (useDemo
        ? { demo: 1, ...extra }
        : { ...(monParams || (selectedConnId ? { connectionId: selectedConnId } : {})), ...extra }),
    [useDemo, monParams, selectedConnId]);
    // 처음부터 다시 받는 기준은 **연결·예시**뿐이다(2026-10-27 신고 — 다른 화면에 다녀오면 리포트가 비어 있었다).
    // 창의 세션은 오래 쉬면 서버가 정리하고 다시 만들어지는데, 그때마다 run·target 이 새 함수가 되어 결과를 지웠다.
    // 조회에는 늘 그 순간의 세션을 쓰도록 ref 로 읽는다
    const runRef = useRef(run);
    runRef.current = run;
    const targetRef = useRef(target);
    targetRef.current = target;
    const connKey = useDemo ? 'demo' : (selectedConnId || (monParams && monParams.connectionId) || '');

    // ── 고르기 ──
    const [dbs, setDbs] = useState([]);
    const [current, setCurrent] = useState(null);
    const [dbKey, setDbKey] = useState('');
    // null = 사용자가 스냅샷·막대로 직접 고른 구간 — 기간과 맞지 않으므로 기간 칸을 비운다(2026-10-14)
    const [days, setDays] = useState(7);
    const [snaps, setSnaps] = useState([]);
    const [beginSnap, setBeginSnap] = useState('');
    const [endSnap, setEndSnap] = useState('');
    const [pickError, setPickError] = useState(null);
    // 다른 DB(들여온 스냅샷 · 같은 저장소의 다른 DB)는 기본으로 숨긴다 — 위의 연결·세션이 가리키는 DB 의 인스턴스만 보인다(2026-10-14)
    const [showOtherDbs, setShowOtherDbs] = useState(false);
    const [fullSql, setFullSql] = useState({});     // sqlId → { status, text?, error? } — 펼친 것만

    // ── 분석 옵션 ──
    const [sections, setSections] = useState(SECTION_KEYS);
    const [withLive, setWithLive] = useState(false);
    const [useRule, setUseRule] = useState(true);
    const [useAi, setUseAi] = useState(false);
    const [includeSqlText, setIncludeSqlText] = useState(false);

    // ── 결과 ──
    const [view, setView] = useState('summary');
    const [busy, setBusy] = useState(null);         // 'report' | 'facts' | null
    const [report, setReport] = useState(null);     // { text, meta }
    const [facts, setFacts] = useState(null);
    const [findings, setFindings] = useState(null);
    const [ai, setAi] = useState(null);             // { status, result?, error? }
    const [error, setError] = useState(null);
    const [sqlView, setSqlView] = useState(null);   // { kind, sql }

    // 이 DB = 지금 연결의 dbid(CDB) 또는 PDB 의 dbid(PDB 단위 AWR)
    const isThisDb = useCallback((d) => !!(current && (d.dbid === current.dbid || (current.conDbid != null && d.dbid === current.conDbid))), [current]);
    const thisDbs = useMemo(() => dbs.filter(isThisDb), [dbs, isThisDb]);
    // 이 DB 를 못 가리면(현재 인스턴스를 못 읽음) 다 보인다 — 아무것도 못 고르는 것보다 낫다
    const shownDbs = showOtherDbs || thisDbs.length === 0 ? dbs : thisDbs;
    const hiddenDbCount = dbs.length - shownDbs.length;
    const db = useMemo(() => dbs.find(d => `${d.dbid}:${d.instanceNumber}` === dbKey) || null, [dbs, dbKey]);
    const isCurrent = !!(db && current && db.dbid === current.dbid && db.instanceNumber === current.instanceNumber);

    // 연결·데모가 바뀌면 처음부터
    useEffect(() => {
        let alive = true;
        setDbs([]); setSnaps([]); setReport(null); setFacts(null); setFindings(null); setAi(null); setError(null); setPickError(null);
        runRef.current(() => axios.get(`${AWR_API}/dbs`, { params: targetRef.current() }))
            .then(r => {
                if (!alive) return;
                const list = r.data?.data || [];
                setDbs(list);
                setCurrent(r.data?.current || null);
                const cur = r.data?.current;
                const mine = list.filter(d => cur && (d.dbid === cur.dbid || (cur.conDbid != null && d.dbid === cur.conDbid)));
                const first = list.find(d => cur && d.dbid === cur.dbid && d.instanceNumber === cur.instanceNumber) || mine[0] || list[0];
                setDbKey(first ? `${first.dbid}:${first.instanceNumber}` : '');
            })
            .catch(err => { if (alive) setPickError(apiError(err, tRef.current)); });
        return () => { alive = false; };
    }, [connKey]);

    useEffect(() => {
        if (!db) return undefined;
        let alive = true;
        setSnaps([]);
        runRef.current(() => axios.get(`${AWR_API}/snapshots`, { params: targetRef.current({ dbid: db.dbid, inst: db.instanceNumber, days: BAR_DAYS }) }))
            .then(r => {
                if (!alive) return;
                setSnaps(r.data?.data || []);
            })
            .catch(err => { if (alive) setPickError(apiError(err, tRef.current)); });
        return () => { alive = false; };
    }, [db]);
    // 기간을 고르면(또는 목록이 새로 오면) 그 기간 전체를 구간으로 — 막대의 손잡이가 함께 움직인다
    useEffect(() => {
        // 직접 고른 구간이면 건드리지 않는다 — 단, 목록이 바뀌어(다른 DB·인스턴스) 고른 스냅샷이 없으면 기본 7일로 돌아간다
        if (days == null) {
            const has = (id) => snaps.some(x => String(x.snapId) === String(id));
            if (has(beginSnap) && has(endSnap)) return;
            setDays(7);
            return;
        }
        const r0 = defaultRange(snaps, days);
        setEndSnap(r0.end);
        setBeginSnap(r0.begin);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snaps, days]);
    // 스냅샷을 직접 고르면(선택 상자·막대) 기간은 더 이상 맞지 않는다 — 기간 칸을 비운다
    const pickRange = (b, e) => { setBeginSnap(b); setEndSnap(e); setDays(null); };

    useEffect(() => { if (!isCurrent) setWithLive(false); }, [isCurrent]);
    // 다른 DB 를 다시 숨기면 고른 것이 목록에서 사라진다 — 이 DB 의 첫 인스턴스로
    useEffect(() => {
        if (db && !shownDbs.includes(db) && shownDbs[0]) setDbKey(`${shownDbs[0].dbid}:${shownDbs[0].instanceNumber}`);
    }, [db, shownDbs]);
    // 결과가 바뀌면 펼친 원문은 접는다
    useEffect(() => { setFullSql({}); }, [facts]);

    const toggleFullSql = async (sqlId) => {
        if (fullSql[sqlId]) { setFullSql(prev => { const n = { ...prev }; delete n[sqlId]; return n; }); return; }
        setFullSql(prev => ({ ...prev, [sqlId]: { status: 'loading' } }));
        try {
            const r = await run(() => axios.get(`${AWR_API}/sql-full`, { params: target({ dbid: facts?.meta?.dbid ?? db?.dbid, sqlId }) }));
            setFullSql(prev => ({ ...prev, [sqlId]: { status: 'done', text: r.data?.sqlText ?? null } }));
        } catch (err) {
            setFullSql(prev => ({ ...prev, [sqlId]: { status: 'error', error: apiError(err, t) } }));
        }
    };

    // 두 스냅샷 사이에 재기동이 있는가 — 고르는 순간 말한다(서버도 거절하지만 누르기 전에 알아야 한다)
    const restartBetween = useMemo(() => {
        const b = snaps.find(s => String(s.snapId) === beginSnap);
        const e = snaps.find(s => String(s.snapId) === endSnap);
        return !!(b && e && b.startup && e.startup && b.startup !== e.startup);
    }, [snaps, beginSnap, endSnap]);
    const rangeOk = beginSnap && endSnap && Number(beginSnap) < Number(endSnap) && !restartBetween;
    const rangeParams = () => target({ dbid: db.dbid, inst: db.instanceNumber, beginSnap, endSnap });

    const toggleSection = (key) => setSections(prev => (prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]));

    const loadReport = async () => {
        if (!db || !rangeOk) return;
        setBusy('report'); setError(null);
        try {
            const r = await run(() => axios.get(`${AWR_API}/report`, { params: rangeParams() }));
            setReport({ text: r.data?.text || '', meta: r.data?.meta || null });
            setView('report');
        } catch (err) { setError(apiError(err, t)); }
        finally { setBusy(null); }
    };

    const analyze = async () => {
        if (!db || !rangeOk || (!useRule && !useAi)) return;
        const picked = [...sections, ...(withLive && isCurrent ? ['live'] : [])];
        if (picked.length === 0) return;
        setBusy('facts'); setError(null); setFacts(null); setFindings(null); setAi(null); setView('summary');
        let got;
        try {
            const r = await run(() => axios.get(`${AWR_API}/facts`, { params: { ...rangeParams(), sections: picked.join(',') } }));
            got = r.data?.facts || null;
            setFacts(got);
            setFindings(useRule ? (r.data?.findings || []) : null);
        } catch (err) {
            setError(apiError(err, t));
            setBusy(null);
            return;
        }
        setBusy(null);
        if (!useAi || !AI_ENABLED || !got) return;

        // AI 는 규칙 결과를 보여 준 뒤에 따로 돈다 — 기다리는 동안 규칙 판정을 읽을 수 있다
        setAi({ status: 'running' });
        try {
            let toSend = got;
            if (includeSqlText && Array.isArray(got.topSql) && got.topSql.length > 0) {
                const ids = got.topSql.map(q => q.sqlId).filter(Boolean).join(',');
                const tx = await run(() => axios.get(`${AWR_API}/sql-texts`, { params: target({ dbid: db.dbid, sqlIds: ids }) }));
                const byId = new Map((tx.data?.data || []).map(x => [x.sqlId, x.sqlText]));
                toSend = { ...got, topSql: got.topSql.map(q => ({ ...q, sqlText: byId.get(q.sqlId) || null })) };
            }
            const provider = (loadSection('assistant') || {}).provider || undefined;
            const result = await summarizeAwr({
                facts: toSend, sections: picked, includeSqlText, provider,
                lang: (i18n.language || 'ko').startsWith('en') ? 'en' : 'ko',
            });
            setAi({ status: 'done', result });
        } catch (err) {
            setAi({ status: 'error', error: apiError(err, t) });
        }
    };

    const saveReport = async () => {
        if (!report) return;
        const m = report.meta || {};
        const name = `awr_${m.dbName || m.dbid || 'db'}_${m.inst ?? m.instanceNumber ?? ''}_${m.beginSnap}_${m.endSnap}.txt`;
        const r = await saveTextFile({ fileName: name, content: report.text, kind: 'text' });
        if (r && !r.ok && !r.canceled) setError(r.detail || r.reason || 'save failed');
    };

    const showSql = async (kind) => {
        if (sqlView && sqlView.kind === kind) { setSqlView(null); return; }
        try {
            const r = await axios.get(`${AWR_API}/source-sql`, { params: { kind } });
            setSqlView({ kind, sql: r.data?.sql || '' });
        } catch (err) { setSqlView({ kind, sql: null, error: apiError(err, t) }); }
    };

    const ruleTitle = (f) => t(`monitoring:awr.rule.${f.code}.title`, f.params || {});
    const ruleDetail = (f) => {
        if (f.code === 'TOP_WAIT') {
            const k = WAIT_CLASS_KEY[f.params?.waitClass] || 'other';
            return `${t('monitoring:awr.rule.TOP_WAIT.detail', f.params || {})} ${t(`monitoring:awr.waitAdvice.${k}`)}`;
        }
        return t(`monitoring:awr.rule.${f.code}.detail`, f.params || {});
    };

    // 섹션별 진단 의견 — 규칙 판정과 AI 판정 가운데 그 섹션의 것(2026-10-14: 각 섹션 옆에 보인다)
    const opinionsOf = (section) => {
        const rule = (findings || []).filter(f => f.section === section).map(f => ({ ...f, by: 'rule', title: ruleTitle(f), detail: ruleDetail(f) }));
        const aiList = ai && ai.status === 'done' ? (ai.result.findings || []).filter(f => f.section === section).map(f => ({ ...f, by: 'ai' })) : [];
        return [...rule, ...aiList];
    };
    const aiUnsectioned = ai && ai.status === 'done' ? (ai.result.findings || []).filter(f => !f.section) : [];
    const anyJudge = findings != null || (ai && ai.status === 'done');
    // 컴포넌트가 아니라 **그리는 함수**다 — 렌더 안에서 컴포넌트를 정의하면 매 렌더 새 타입이라 상자가 다시 붙고 페이드 인이 되풀이돼 깜빡였다(2026-10-14 신고)
    const renderOpinion = (section) => {
        if (!anyJudge) return null;
        const list = opinionsOf(section);
        return (
            <div className={`awr-opinion${OPINION_BELOW.has(section) ? ' is-below' : ''}`}>
                <div className="awr-opinion-title">{t('monitoring:awr.opinion')}</div>
                {list.length === 0
                    ? <div className="awr-muted">{t('monitoring:awr.noOpinion')}</div>
                    : list.map((f, i) => (
                        <div key={`${f.code || f.title}-${i}`} className={`awr-finding sev-${f.severity}`}>
                            <span className="awr-sev">{t(`monitoring:awr.sev.${f.severity}`)}</span>
                            <div>
                                <div className="awr-finding-title">
                                    {f.title}
                                    {f.by === 'ai' && <span className="awr-by-ai"><FiCpu size={10} /> AI</span>}
                                </div>
                                <div className="awr-finding-detail">{f.detail}</div>
                                {f.sqlIds && f.sqlIds.length > 0 && <div className="awr-finding-sql">SQL_ID: {f.sqlIds.join(', ')}</div>}
                            </div>
                        </div>
                    ))}
            </div>
        );
    };
    // 표와 의견을 나란히(넓으면) — Top SQL 은 의견을 표 아래에
    const secBody = (section, children) => (
        <div className={`awr-sec-body${anyJudge && !OPINION_BELOW.has(section) ? ' has-opinion' : ''}`}>
            <div className="awr-sec-main">{children}</div>
            {renderOpinion(section)}
        </div>
    );
    const goSection = (section) => {
        const el = document.getElementById(`awr-sec-${section}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const sectionHead = (title, kind) => (
        <div className="awr-sec-head">
            <span>{title}</span>
            <QueryViewButton onClick={() => showSql(kind)} title={t('monitoring:awr.showSql')} />
        </div>
    );
    const sqlBox = (kind) => (sqlView && sqlView.kind === kind
        ? <SqlSourceView className="awr-sqlbox" sql={sqlView.sql} error={sqlView.error} loadingText="" />
        : null);

    return (
        <div className="awr-panel">
            {/* ── 고르기 ── */}
            <div className="awr-toolbar">
                <label className="awr-field">
                    <span>{t('monitoring:awr.db')}</span>
                    <select value={dbKey} onChange={(e) => setDbKey(e.target.value)} disabled={dbs.length === 0}>
                        {dbs.length === 0 && <option value="">{t('monitoring:awr.noDb')}</option>}
                        {shownDbs.map(d => (
                            <option key={`${d.dbid}:${d.instanceNumber}`} value={`${d.dbid}:${d.instanceNumber}`}>
                                {`${d.dbName} (${d.dbid}) · ${d.instanceName || d.instanceNumber}`}
                                {isThisDb(d) || !current ? '' : ` · ${d.isLocal ? t('monitoring:awr.otherDb') : t('monitoring:awr.imported')}`}
                                {current && d.dbid === current.dbid && d.instanceNumber === current.instanceNumber ? ` · ${t('monitoring:awr.currentInst')}` : ''}
                            </option>
                        ))}
                    </select>
                </label>
                {(hiddenDbCount > 0 || showOtherDbs) && dbs.length > thisDbs.length && thisDbs.length > 0 && (
                    <label className="awr-check" title={t('monitoring:awr.otherDbsHint')}>
                        <input type="checkbox" checked={showOtherDbs} onChange={(e) => setShowOtherDbs(e.target.checked)} />
                        {t('monitoring:awr.showOtherDbs', { n: dbs.length - thisDbs.length })}
                    </label>
                )}
                <label className="awr-field">
                    <span>{t('monitoring:awr.days')}</span>
                    <select value={days ?? ''} onChange={(e) => setDays(e.target.value === '' ? null : Number(e.target.value))}>
                        {days == null && <option value="" hidden aria-label={t('monitoring:awr.daysCustom')}></option>}
                        {DAYS.map(d => <option key={d} value={d}>{t('monitoring:awr.daysN', { n: d })}</option>)}
                    </select>
                </label>
                <label className="awr-field">
                    <span>{t('monitoring:awr.beginSnap')}</span>
                    <select value={beginSnap} onChange={(e) => pickRange(e.target.value, endSnap)} disabled={snaps.length === 0}>
                        {snaps.map(s => <option key={s.snapId} value={s.snapId}>{`${s.snapId} · ${shortTime(s.endTime)}`}</option>)}
                    </select>
                </label>
                <label className="awr-field">
                    <span>{t('monitoring:awr.endSnap')}</span>
                    <select value={endSnap} onChange={(e) => pickRange(beginSnap, e.target.value)} disabled={snaps.length === 0}>
                        {snaps.map(s => <option key={s.snapId} value={s.snapId}>{`${s.snapId} · ${shortTime(s.endTime)}`}</option>)}
                    </select>
                </label>
                <div className="awr-spacer" />
                <label className="awr-check">
                    <input type="checkbox" checked={useDemo} onChange={(e) => setUseDemo(e.target.checked)} />
                    {t('monitoring:awr.demo')}
                </label>
            </div>

            {/* 스냅샷 구간 막대 — 양 끝이 기간의 첫·마지막 스냅샷, 세로 막대 둘을 끌어 시작·끝을 고른다 */}
            {snaps.length > 1 && (
                <SnapRangeBar
                    snaps={snaps}
                    beginSnap={beginSnap}
                    endSnap={endSnap}
                    disabled={!!busy}
                    onChange={pickRange}
                />
            )}
            {pickError && <div className="awr-error"><FiAlertTriangle size={13} /> {pickError}</div>}
            {snaps.length > 0 && !rangeOk && (
                <div className="awr-warn">
                    <FiAlertTriangle size={13} /> {restartBetween ? t('monitoring:awr.restartBetween') : t('monitoring:awr.badRange')}
                </div>
            )}

            {/* ── 분석 옵션 ── */}
            <div className="awr-options">
                <div className="awr-opt-group">
                    <span className="awr-opt-label">{t('monitoring:awr.sections')}</span>
                    {SECTION_KEYS.map(k => (
                        <label key={k} className="awr-check">
                            <input type="checkbox" checked={sections.includes(k)} onChange={() => toggleSection(k)} />
                            {t(`monitoring:awr.section.${k}`)}
                        </label>
                    ))}
                    <label className="awr-check" title={isCurrent ? t('monitoring:awr.liveHint') : t('monitoring:awr.liveDisabled')}>
                        <input type="checkbox" checked={withLive} disabled={!isCurrent} onChange={(e) => setWithLive(e.target.checked)} />
                        {t('monitoring:awr.section.live')}
                    </label>
                </div>
                <div className="awr-opt-group">
                    <span className="awr-opt-label">{t('monitoring:awr.method')}</span>
                    <label className="awr-check">
                        <input type="checkbox" checked={useRule} onChange={(e) => setUseRule(e.target.checked)} />
                        {t('monitoring:awr.ruleBased')}
                    </label>
                    {AI_ENABLED && (
                        <>
                            <label className="awr-check">
                                <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} />
                                {t('monitoring:awr.aiBased')} <span className="awr-ai-badge">{t('monitoring:awr.aiUse')}</span>
                            </label>
                            <label className="awr-check" title={t('monitoring:awr.includeSqlHint')}>
                                <input type="checkbox" checked={includeSqlText} disabled={!useAi || !sections.includes('topSql')}
                                    onChange={(e) => setIncludeSqlText(e.target.checked)} />
                                {t('monitoring:awr.includeSql')}
                            </label>
                        </>
                    )}
                </div>
                <div className="awr-actions">
                    <button type="button" className="app-btn" onClick={loadReport} disabled={!rangeOk || !!busy}>
                        <FiFileText size={12} /> {busy === 'report' ? t('monitoring:awr.working') : t('monitoring:awr.makeReport')}
                    </button>
                    <button type="button" className="app-btn app-btn-primary" onClick={analyze}
                        disabled={!rangeOk || !!busy || (!useRule && !useAi) || (sections.length === 0 && !withLive)}>
                        <FiPlay size={12} /> {busy === 'facts' ? t('monitoring:awr.working') : t('monitoring:awr.analyze')}
                    </button>
                </div>
            </div>
            <div className="awr-license"><FiInfo size={12} /> {t('monitoring:awr.license')}</div>

            {error && <div className="awr-error"><FiAlertTriangle size={13} /> {error}</div>}

            {/* ── 결과 ── */}
            <div className="awr-viewtabs">
                <button type="button" className={view === 'summary' ? 'active' : ''} onClick={() => setView('summary')}>{t('monitoring:awr.viewSummary')}</button>
                <button type="button" className={view === 'report' ? 'active' : ''} onClick={() => setView('report')}>{t('monitoring:awr.viewReport')}</button>
            </div>

            <div className="awr-body">
                {view === 'report' ? (
                    report ? (
                        <div className="awr-report">
                            <div className="awr-report-bar">
                                <span>{`${report.meta?.dbName || report.meta?.dbid || ''} · ${report.meta?.beginSnap} → ${report.meta?.endSnap}`}</span>
                                <button type="button" className="app-btn" onClick={saveReport}><FiDownload size={12} /> {t('monitoring:awr.saveTxt')}</button>
                            </div>
                            <pre className="awr-report-text">{report.text}</pre>
                        </div>
                    ) : <div className="awr-empty">{t('monitoring:awr.noReport')}</div>
                ) : (
                    !facts ? <div className="awr-empty">{busy === 'facts' ? t('monitoring:awr.working') : t('monitoring:awr.noAnalysis')}</div> : (
                        <div className="awr-summary">
                            <div className="awr-meta">
                                {`${facts.meta?.dbName || facts.meta?.dbid} · inst ${facts.meta?.instanceNumber} · ${facts.meta?.beginSnap} → ${facts.meta?.endSnap} · ${shortTime(facts.meta?.beginTime)} ~ ${shortTime(facts.meta?.endTime)}`}
                                <span className="awr-meta-sub">{t('monitoring:awr.elapsedAas', { min: fmt((facts.meta?.elapsedSec || 0) / 60, 1), aas: fmt(facts.meta?.aas) })}</span>
                                {facts.meta?.scope === 'pdb' && <span className="awr-meta-sub" title={t('monitoring:awr.pdbScopeHint')}>{t('monitoring:awr.pdbScope')}</span>}
                            </div>

                            <div className="awr-columns">
                                {findings && (
                                    <div className="awr-card">
                                        <div className="awr-card-title">{t('monitoring:awr.ruleResult')}</div>
                                        {/* 자세한 의견은 각 섹션 옆에 있다 — 여기는 제목만, 누르면 그 섹션으로 */}
                                        {findings.length === 0
                                            ? <div className="awr-muted">{t('monitoring:awr.noFinding')}</div>
                                            : findings.map((f, i) => (
                                                <button key={`${f.code}-${i}`} type="button" className={`awr-finding awr-finding-link sev-${f.severity}`} onClick={() => goSection(f.section)}>
                                                    <span className="awr-sev">{t(`monitoring:awr.sev.${f.severity}`)}</span>
                                                    <span className="awr-finding-title">{ruleTitle(f)}</span>
                                                    <span className="awr-finding-sec">{t(`monitoring:awr.section.${f.section}`)}</span>
                                                </button>
                                            ))}
                                    </div>
                                )}
                                {ai && (
                                    <div className="awr-card">
                                        <div className="awr-card-title"><FiCpu size={12} /> {t('monitoring:awr.aiResult')}</div>
                                        {ai.status === 'running' && <div className="awr-muted">{t('monitoring:awr.aiRunning')}</div>}
                                        {ai.status === 'error' && <div className="awr-error">{ai.error}</div>}
                                        {ai.status === 'done' && (
                                            <>
                                                {ai.result.sentSqlText && <div className="awr-warn small"><FiInfo size={12} /> {t('monitoring:awr.sentSqlText')}</div>}
                                                <p className="awr-ai-summary">{ai.result.summary}</p>
                                                {/* 섹션이 있는 판정은 그 섹션 옆에 보인다 — 여기는 섹션을 정하지 못한 것만 */}
                                                {(ai.result.findings || []).filter(f => f.section).length > 0 && (
                                                    <div className="awr-muted small">{t('monitoring:awr.aiPerSection', { n: (ai.result.findings || []).filter(f => f.section).length })}</div>
                                                )}
                                                {aiUnsectioned.map((f, i) => (
                                                    <div key={i} className={`awr-finding sev-${f.severity}`}>
                                                        <span className="awr-sev">{t(`monitoring:awr.sev.${f.severity}`)}</span>
                                                        <div>
                                                            <div className="awr-finding-title">{f.title}</div>
                                                            <div className="awr-finding-detail">{f.detail}</div>
                                                            {f.sqlIds && f.sqlIds.length > 0 && <div className="awr-finding-sql">SQL_ID: {f.sqlIds.join(', ')}</div>}
                                                        </div>
                                                    </div>
                                                ))}
                                                {(ai.result.nextSteps || []).length > 0 && (
                                                    <ul className="awr-next">{ai.result.nextSteps.map((x, i) => <li key={i}>{x}</li>)}</ul>
                                                )}
                                                {ai.result.rejected && ai.result.rejected.length > 0 && (
                                                    <div className="awr-muted small">{t('monitoring:awr.rejected', { ids: ai.result.rejected.map(r => r.sqlId).join(', ') })}</div>
                                                )}
                                                {ai.result.usage && (
                                                    <div className="awr-muted small">
                                                        {t('monitoring:awr.usage', {
                                                            input: fmt(ai.result.usage.inputTokens, 0), output: fmt(ai.result.usage.outputTokens, 0),
                                                            cost: typeof ai.result.usage.costUsd === 'number' ? `$${ai.result.usage.costUsd.toFixed(4)}` : '-',
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {facts.timeModel && (
                                <div className="awr-sec" id="awr-sec-timeModel">
                                    {sectionHead(t('monitoring:awr.section.timeModel'), 'timeModel')}
                                    {sqlBox('timeModel')}
                                    {secBody('timeModel', (<>
                                    <table className="awr-table"><tbody>
                                        {[['dbTimeSec', 'sec'], ['dbCpuSec', 'sec'], ['bgCpuSec', 'sec'], ['sqlExecSec', 'sec'], ['parseSec', 'sec'], ['hardParseSec', 'sec'], ['plsqlSec', 'sec'], ['dbCpuPct', 'pct'], ['hardParsePct', 'pct']].map(([k, u]) => (
                                            <tr key={k}><th>{t(`monitoring:awr.tm.${k}`)}</th><td className="num">{fmt(facts.timeModel[k])}{u === 'pct' ? ' %' : ''}</td></tr>
                                        ))}
                                    </tbody></table>
                                    </>))}
                                </div>
                            )}

                            {facts.loadProfile && (
                                <div className="awr-sec" id="awr-sec-loadProfile">
                                    {sectionHead(t('monitoring:awr.section.loadProfile'), 'loadProfile')}
                                    {sqlBox('loadProfile')}
                                    {secBody('loadProfile', (<>
                                    <table className="awr-table">
                                        <thead><tr><th>{t('monitoring:awr.col.stat')}</th><th className="num">{t('monitoring:awr.col.perSec')}</th><th className="num">{t('monitoring:awr.col.perTx')}</th><th className="num">{t('monitoring:awr.col.total')}</th></tr></thead>
                                        <tbody>
                                            {facts.loadProfile.items.map(it => (
                                                <tr key={it.name}><td className="mono">{it.name}</td><td className="num">{fmt(it.perSec)}</td><td className="num">{fmt(it.perTx)}</td><td className="num">{fmt(it.total, 0)}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </>))}
                                </div>
                            )}

                            {facts.waitEvents && (
                                <div className="awr-sec" id="awr-sec-waitEvents">
                                    {sectionHead(t('monitoring:awr.section.waitEvents'), 'waitEvents')}
                                    {sqlBox('waitEvents')}
                                    {secBody('waitEvents', (<>
                                    <table className="awr-table">
                                        <thead><tr><th>{t('monitoring:awr.col.event')}</th><th>{t('monitoring:awr.col.waitClass')}</th><th className="num">{t('monitoring:awr.col.waits')}</th><th className="num">{t('monitoring:awr.col.timeSec')}</th><th className="num">{t('monitoring:awr.col.avgMs')}</th><th className="num">{t('monitoring:awr.col.pctDbTime')}</th></tr></thead>
                                        <tbody>
                                            <tr className="awr-cpu-row"><td className="mono">DB CPU</td><td /><td /><td className="num">{fmt(facts.waitEvents.dbCpuSec)}</td><td /><td className="num">{fmt(facts.waitEvents.dbCpuPct, 1)}</td></tr>
                                            {facts.waitEvents.top.map(e => (
                                                <tr key={e.event}><td className="mono">{e.event}</td><td>{e.waitClass}</td><td className="num">{fmt(e.waits, 0)}</td><td className="num">{fmt(e.timeSec)}</td><td className="num">{fmt(e.avgMs)}</td><td className="num">{fmt(e.pctDbTime, 1)}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </>))}
                                </div>
                            )}

                            {facts.topSql && (
                                <div className="awr-sec" id="awr-sec-topSql">
                                    {sectionHead(t('monitoring:awr.section.topSql'), 'topSql')}
                                    {sqlBox('topSql')}
                                    {secBody('topSql', (<>
                                    <div className="awr-table-scroll">
                                    <table className="awr-table">
                                        <thead><tr>
                                            <th>SQL_ID</th><th>{t('monitoring:awr.col.module')}</th>
                                            <th className="num">{t('monitoring:awr.col.execs')}</th><th className="num">{t('monitoring:awr.col.elapsedSec')}</th>
                                            <th className="num">{t('monitoring:awr.col.pctDbTime')}</th><th className="num">{t('monitoring:awr.col.perExecSec')}</th>
                                            <th className="num">{t('monitoring:awr.col.gets')}</th><th className="num">{t('monitoring:awr.col.diskReads')}</th>
                                            <th className="num">{t('monitoring:awr.col.plans')}</th><th>{t('monitoring:awr.col.sqlHead')}</th>
                                        </tr></thead>
                                        <tbody>
                                            {facts.topSql.map(q => (
                                                <React.Fragment key={q.sqlId}>
                                                <tr>
                                                    <td className="mono">{q.sqlId}</td><td>{q.module || '-'}</td>
                                                    {/* 구간 안에 안 끝난 실행은 실행당 값이 「없다」 — 0 으로 보이면 판단이 뒤집힌다 */}
                                                    <td className="num">{q.executions === 0 ? <span className="awr-zero-exec" title={t('monitoring:awr.zeroExecHint')}>0</span> : fmt(q.executions, 0)}</td>
                                                    <td className="num">{fmt(q.elapsedSec)}</td><td className="num">{fmt(q.pctDbTime, 1)}</td>
                                                    <td className="num">{fmt(q.elapsedPerExecSec, 4)}</td><td className="num">{fmt(q.bufferGets, 0)}</td>
                                                    <td className="num">{fmt(q.diskReads, 0)}</td>
                                                    <td className={`num ${q.planCount > 1 ? 'awr-multi-plan' : ''}`}>{fmt(q.planCount, 0)}</td>
                                                    <td className="mono awr-sqlhead">
                                                        {/* 앞 200자만 싣는다 — 누르면 전체 원문을 받아 아래 줄에 편다(화면 전용, AI 로 가지 않는다) */}
                                                        <button type="button" className="awr-sqlhead-btn" onClick={() => toggleFullSql(q.sqlId)}
                                                            aria-expanded={!!fullSql[q.sqlId]} title={t('monitoring:awr.fullSqlHint')}>
                                                            <span className="awr-sqlhead-text">{q.sqlTextHead || '-'}</span>
                                                            <span className="awr-sqlhead-more">{fullSql[q.sqlId] ? t('monitoring:awr.fullSqlHide') : t('monitoring:awr.fullSql')}</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                                {fullSql[q.sqlId] && (
                                                    <tr className="awr-fullsql-row">
                                                        <td colSpan={10}>
                                                            {fullSql[q.sqlId].status === 'loading' && <div className="awr-muted">{t('monitoring:awr.working')}</div>}
                                                            {fullSql[q.sqlId].status === 'error' && <div className="awr-error">{fullSql[q.sqlId].error}</div>}
                                                            {fullSql[q.sqlId].status === 'done' && (fullSql[q.sqlId].text
                                                                ? <SqlSourceView className="awr-fullsql" sql={fullSql[q.sqlId].text} loadingText="" />
                                                                : <div className="awr-muted">{t('monitoring:awr.fullSqlNone')}</div>)}
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                    </div>
                                    </>))}
                                </div>
                            )}

                            {[['segLogical', 'logicalReads'], ['segPhysical', 'physicalReads']].map(([sec, key]) => facts[sec] && (
                                <div key={sec} className="awr-sec" id={`awr-sec-${sec}`}>
                                    {sectionHead(t(`monitoring:awr.section.${sec}`), sec)}
                                    {sqlBox(sec)}
                                    {secBody(sec, (<>
                                    {facts[sec].length === 0 ? <div className="awr-muted">{t('monitoring:awr.noSegData')}</div> : (
                                        <table className="awr-table">
                                            <thead><tr>
                                                <th>{t('monitoring:awr.col.object')}</th><th>{t('monitoring:awr.col.objType')}</th>
                                                <th className={`num${key === 'logicalReads' ? ' awr-col-key' : ''}`}>{t('monitoring:awr.col.logicalReads')}</th>
                                                <th className={`num${key === 'physicalReads' ? ' awr-col-key' : ''}`}>{t('monitoring:awr.col.physicalReads')}</th>
                                                <th className="num" title={t('monitoring:awr.pctTotalHint')}>{t('monitoring:awr.col.pctTotal')}</th>
                                            </tr></thead>
                                            <tbody>
                                                {facts[sec].map(x => (
                                                    <tr key={`${x.objNo}-${x.subobjectName || ''}`}>
                                                        <td className="mono">{segLabel(x, t)}</td><td>{x.objectType || '-'}</td>
                                                        <td className="num">{fmt(x.logicalReads, 0)}</td><td className="num">{fmt(x.physicalReads, 0)}</td>
                                                        <td className="num">{fmt(x.pctTotal, 1)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    </>))}
                                </div>
                            ))}

                            {facts.tablespaceIo && (
                                <div className="awr-sec" id="awr-sec-tablespaceIo">
                                    {sectionHead(t('monitoring:awr.section.tablespaceIo'), 'tablespaceIo')}
                                    {sqlBox('tablespaceIo')}
                                    {secBody('tablespaceIo', (<>
                                    {facts.tablespaceIo.length === 0 ? <div className="awr-muted">{t('monitoring:awr.noTbsData')}</div> : (
                                        <table className="awr-table">
                                            <thead><tr>
                                                <th>{t('monitoring:awr.col.tablespace')}</th><th className="num">{t('monitoring:awr.col.reads')}</th>
                                                <th className="num">{t('monitoring:awr.col.readsPerSec')}</th><th className="num">{t('monitoring:awr.col.avgReadMs')}</th>
                                                <th className="num">{t('monitoring:awr.col.writes')}</th>
                                            </tr></thead>
                                            <tbody>
                                                {facts.tablespaceIo.map(x => (
                                                    <tr key={x.tablespace}>
                                                        <td className="mono">{x.tablespace}</td><td className="num">{fmt(x.reads, 0)}</td>
                                                        <td className="num">{fmt(x.readsPerSec)}</td><td className="num">{fmt(x.avgReadMs)}</td>
                                                        <td className="num">{fmt(x.writes, 0)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    </>))}
                                </div>
                            )}

                            {facts.efficiency && (
                                <div className="awr-sec" id="awr-sec-efficiency">
                                    {/* 자기 kind 를 써야 SQL 상자가 이 섹션에 펼쳐진다 — 예전에는 loadProfile 이라 위쪽 섹션에 열려 「안 보인다」 였다 */}
                                    {sectionHead(t('monitoring:awr.section.efficiency'), 'efficiency')}
                                    {sqlBox('efficiency')}
                                    {secBody('efficiency', (<>
                                    <table className="awr-table"><tbody>
                                        {['bufferHitPct', 'softParsePct', 'executeToParsePct', 'rollbackPct', 'hardParsesPerSec', 'transactionsPerSec'].map(k => (
                                            <tr key={k}><th>{t(`monitoring:awr.eff.${k}`)}</th><td className="num">{fmt(facts.efficiency[k])}{k.endsWith('Pct') ? ' %' : ''}</td></tr>
                                        ))}
                                    </tbody></table>
                                    </>))}
                                </div>
                            )}

                            {facts.liveSkipped && <div className="awr-muted">{t('monitoring:awr.liveSkipped')}</div>}
                            {facts.live && (
                                <div className="awr-sec" id="awr-sec-live">
                                    {sectionHead(t('monitoring:awr.section.live'), 'live')}
                                    {sqlBox('live')}
                                    <div className="awr-live">
                                        <table className="awr-table">
                                            <thead><tr><th className="num">SGA MB</th><th className="num">{t('monitoring:awr.col.factor')}</th><th className="num">{t('monitoring:awr.col.estdDbTime')}</th></tr></thead>
                                            <tbody>{facts.live.sgaAdvice.map(a => (
                                                <tr key={a.factor} className={a.factor === 1 ? 'awr-cur-row' : ''}><td className="num">{fmt(a.sizeMb, 0)}</td><td className="num">{fmt(a.factor)}</td><td className="num">{fmt(a.estdDbTime, 0)}</td></tr>
                                            ))}</tbody>
                                        </table>
                                        <table className="awr-table">
                                            <thead><tr><th className="num">PGA MB</th><th className="num">{t('monitoring:awr.col.factor')}</th><th className="num">{t('monitoring:awr.col.cacheHit')}</th><th className="num">{t('monitoring:awr.col.overalloc')}</th></tr></thead>
                                            <tbody>{facts.live.pgaAdvice.map(a => (
                                                <tr key={a.factor} className={a.factor === 1 ? 'awr-cur-row' : ''}><td className="num">{fmt(a.sizeMb, 0)}</td><td className="num">{fmt(a.factor)}</td><td className="num">{fmt(a.cacheHitPct, 1)}</td><td className="num">{fmt(a.overallocCount, 0)}</td></tr>
                                            ))}</tbody>
                                        </table>
                                    </div>
                                    {renderOpinion('live')}
                                </div>
                            )}
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

AwrReportPanel.propTypes = {
    selectedConnId: PropTypes.string,
    monParams: PropTypes.object,
    monRun: PropTypes.func,
};
